import { ActivatedRoute, ActivatedRouteSnapshot, Params, Route, UrlSegment } from '@angular/router';
import { DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { RealSettingOrigin } from '@cco/model';
import { Signal } from '@angular/core';

export interface Breadcrumb {
  path: any[];
  label?: string;
  label$?: Observable<string>;
  // -eslint-disable-next-line @typescript-eslint/ban-types
  labelSelector?: MemoizedSelector<object, string | number, DefaultProjectorFn<string | number>>;
  labelSignal?: Signal<string>;

  /**
   * Do not manually populate this.
   * @See layout.component.ts
   */
  routerLink?: string | any[];
}

export interface AppRouteData {
  /**
   * Navigate to this relative path when the back button is activated
   */
  backPath?: any[];

  breadcrumbs?: Breadcrumb[];

  /**
   * When this route is activated, clear the selection based on the key (e.g., 'customerId')
   */
  clearSelectionKey?: string;

  /**
   * Navigated to by FlexRoutingGuard when the media changes to gt-sm
   */
  fullPath?: any[];

  /**
   * The semver of the minimum component versions to require before allowing navigation
   */
  requiredVersions?: {
    [componentName: string]: string;
  };

  /**
   * Navigated to by FlexRoutingGuard when the media changes to lt-md
   */
  mobilePath?: any[];

  /**
   * PrivilegeGuard requires all the privileges before allowing navigation
   */
  requiredPrivileges?: string[] | string;

  /**
   * The prefix to prepend to the route navigated to on item selection.
   * Used to open children items in a parent view.
   * E.g., from Customers > Customer > Projects
   *         to Project > Project
   */
  selectRoutingPrefix?: any[];

  /**
   * Specify origin to manage settings for: USER or COMPANY
   */
  settingsOrigin?: RealSettingOrigin;

  /**
   * Set to true when a component that would normally display in side-by-side panes
   * should be displayed in a single pane.
   */
  singlePane?: boolean;
}

export interface AppRoute extends Route {
  children?: AppRoute[];
  data?: AppRouteData;
}

export type AppRoutes = AppRoute[];

export function getParameterTokens(pathSegment: string): string[] {
  return pathSegment.match(/(:[a-zA-Z]+)/g);
}

export function getParameterName(parameterToken: string): string {
  return parameterToken.slice(1);
}

function getAlternateRoute(
  routeSnapshot: ActivatedRouteSnapshot,
  alternateRouteAccessor: (routeData: any) => any[],
): any[] {
  const snapshots: ActivatedRouteSnapshot[] = [];
  const urlSegments: UrlSegment[][] = [];
  let node = routeSnapshot.root;
  while (node) {
    urlSegments.push(node.url);
    snapshots.push(node);
    node = node.firstChild;
  }

  const commands = [];

  urlSegments.forEach((urlSegment, i) => {
    if (snapshots[i].data && alternateRouteAccessor(snapshots[i].data)) {
      alternateRouteAccessor(snapshots[i].data).forEach((pathSegment) => {
        let segment = pathSegment;
        if (typeof segment === 'string') {
          const parameterTokens = getParameterTokens(segment);
          if (parameterTokens) {
            parameterTokens.forEach((parameterToken) => {
              segment = (segment as string).replace(
                parameterToken,
                snapshots[i].params[getParameterName(parameterToken)],
              );
            });
          }
        }
        commands.push(segment);
      });
    }

    urlSegment.forEach((urlSeg) => {
      if (!snapshots[i].data || !alternateRouteAccessor(snapshots[i].data)) {
        commands.push(urlSeg.path);
      }
      if (Object.getOwnPropertyNames(urlSeg.parameters).length > 0) {
        commands.push(urlSeg.parameters);
      }
    });
  });

  return commands;
}

export function getMobileRoute(snapshot: ActivatedRouteSnapshot): any[] {
  return getAlternateRoute(snapshot, routeData => routeData.mobilePath);
}

export function getFullRoute(snapshot: ActivatedRouteSnapshot): any[] {
  return getAlternateRoute(snapshot, routeData => routeData.fullPath);
}


export function getDescendantsPath(route: ActivatedRoute): any[] {
  const descendantsPath = [];

  let child = route.snapshot.firstChild;
  while (child) {
    if (child.url) {
      child.url.forEach((urlSegment) => {
        if (urlSegment.path) {
          descendantsPath.push(urlSegment.path);
        }
        if (urlSegment.parameters && Object.getOwnPropertyNames(urlSegment.parameters).length > 0) {
          descendantsPath.push(urlSegment.parameters);
        }
      });
    }

    child = child.firstChild;
  }

  return descendantsPath;
}

export function routeParamsFromRoute(route: ActivatedRoute): Observable<Params[]> {
  return of(route.pathFromRoot.map(r => r.params));
}

export function getFirstChildRouteDataArray(snapshot: ActivatedRouteSnapshot): AppRouteData[] {
  let node = snapshot.root;
  const routeData: AppRouteData[] = [];

  do {
    routeData.push(node.data as AppRouteData);
    node = node.firstChild;
  } while (node);

  return routeData;
}

export function getRouteParamFromLeafToRoot(snapshot: ActivatedRouteSnapshot, paramName: string): string | undefined {
  let node = snapshot;
  do {
    if (node.paramMap.has(paramName))
      return node.paramMap.get(paramName);

    node = node.parent;
  } while (node && node !== node.root);

  return undefined;
}
